// src/pages/Customer/CustomerShell.tsx  (mevcut dosya yolunuza uyarlayın)
import React, { useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, MapPin, UserRound, LogOut, KeyRound, Heart } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";

/* ============ Theme helpers ============ */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface1: isDark ? "" : "",
    surface2: isDark ? ""  : "",
    surface3: isDark ? "" : "",
    divider:  isDark ? ""  : "",
    softShadow: isDark ? "" : "",
    textPrimary: theme?.textPrimary ?? (isDark ? "" : ""),
    textSecondary: theme?.textSecondary ?? (isDark ? "" : ""),
    accent: theme?.accent ?? "",
    gradient: theme?.gradientHero ?? "",
  };
};
const panel = (lvl: 1 | 2 | 3, S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: lvl === 1 ? S.surface1 : lvl === 2 ? S.surface2 : S.surface3,
  borderColor: S.divider,
  borderWidth: 1,
  boxShadow: S.softShadow,
});

/* ============ Side item ============ */
const SideItem: React.FC<{
  to: string;
  label: string;
  icon: React.ReactNode;
  S: ReturnType<typeof useSurfaces>;
}> = ({ to, label, icon, S }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      [
        "flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors",
        isActive ? "ring-1 ring-inset" : "hover:opacity-90",
      ].join(" ")
    }
    style={({ isActive }) => ({
      ...panel(1, S),
      backgroundColor: isActive ? S.surface3 : S.surface1,
      color: S.textPrimary,
      borderColor: S.divider,
    })}
  >
    <span className="shrink-0" style={{ color: S.textPrimary }}>{icon}</span>
    <span className="truncate" style={{ color: S.textPrimary }}>{label}</span>
  </NavLink>
);

const CustomerShell: React.FC = () => {
  const { theme } = useTheme();
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const S = useSurfaces(theme);

  const gradient = S.gradient;
  const secondary = S.textSecondary;
  const primary = S.textPrimary;

  const displayName = useMemo(() => {
    const fullName = [customer?.ad, customer?.soyad].filter(Boolean).join(" ").trim();
    if (fullName) return fullName;
    if (customer?.name?.trim()) return customer.name;
    if (customer?.id) return `Müşteri #${customer.id}`;
    return "Hesabım";
  }, [customer]);

  const initials = useMemo(() => {
    const a = customer?.ad?.[0] ?? customer?.name?.trim().split(/\s+/)[0]?.[0] ?? "";
    const b = customer?.soyad?.[0] ?? customer?.name?.trim().split(/\s+/)[1]?.[0] ?? "";
    return (a + b).toUpperCase() || "M";
  }, [customer]);

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div style={{ backgroundColor: S.bg }}>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* HERO */}
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: S.divider, boxShadow: S.softShadow }}>
          <div className="p-[1px]" style={{ background:S.gradient }}>
            <div
              className="p-5 md:p-6 rounded-2xl"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 md:gap-5">
                  <div
                    className="w-12 h-12 md:w-14 md:h-14 rounded-full grid place-items-center text-lg font-semibold shadow-sm"
                    style={{ background: "rgba(255,255,255,.95)", color: "#111827" }}
                  >
                    {initials}
                  </div>
                  <div className="text-white">
                    <h1 className="text-xl md:text-2xl font-semibold leading-tight">
                      Merhaba{displayName ? `, ${displayName}` : ""}!
                    </h1>
                    <p className="text-xs md:text-sm opacity-90">
                      {customer?.id
                        ? `Müşteri No: #${customer.id}`
                        : "Hesabınızı yönetin, sipariş ve adreslerinize göz atın."}
                    </p>
                  </div>
                </div>

                <button
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium shadow-sm transition"
                  style={{ background: "rgba(255,255,255,.95)", color: "" }}
                  title="Çıkış Yap"
                >
                  <LogOut size={16} />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobil sekmeler */}
          <div className="md:hidden px-2 border-t" style={{ borderColor: S.divider, backgroundColor: S.surface1 }}>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-2">
              {[
                { to: "/musteri/panel",            label: "Özet" },
                { to: "/musteri/panel/siparisler", label: "Siparişler" },
                { to: "/musteri/panel/favoriler",  label: "Favoriler" },
                { to: "/musteri/panel/adresler",   label: "Adresler" },
                { to: "/musteri/panel/profil",     label: "Profil" },
                { to: "/musteri/panel/sifre",      label: "Şifre" },
              ].map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.to === "/musteri/panel"}
                  className={({ isActive }) =>
                    "whitespace-nowrap px-3 py-2 rounded-md text-sm border transition"
                  }
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? S.surface3 : S.surface1,
                    color: S.textPrimary,
                    borderColor: S.divider,
                  })}
                >
                  {t.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* Masaüstü düzeni */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="hidden md:block md:col-span-3">
            <div className="rounded-xl p-2 border" style={{ ...panel(2, S) }}>
              <div className="px-2 py-2 text-xs font-medium" style={{ color: secondary }}>
                Navigasyon
              </div>
              <div className="flex flex-col gap-2">
                <SideItem
                  to="/musteri/panel"
                  label="Özet"
                  icon={<LayoutDashboard size={18} />}
                  S={S}
                />
                <SideItem
                  to="/musteri/panel/siparisler"
                  label="Siparişlerim"
                  icon={<Package size={18} />}
                  S={S}
                />
                <SideItem
                  to="/musteri/panel/favoriler"
                  label="Favorilerim"
                  icon={<Heart size={18} />}
                  S={S}
                />
                <SideItem
                  to="/musteri/panel/adresler"
                  label="Adreslerim"
                  icon={<MapPin size={18} />}
                  S={S}
                />
                <SideItem
                  to="/musteri/panel/profil"
                  label="Profil"
                  icon={<UserRound size={18} />}
                  S={S}
                />
                <SideItem
                  to="/musteri/panel/sifre"
                  label="Şifre Değiştir"
                  icon={<KeyRound size={18} />}
                  S={S}
                />
              </div>

              <div className="h-px my-3" style={{ backgroundColor: S.divider }} />
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors"
                style={{ ...panel(1, S), color: primary }}
                title="Çıkış Yap"
              >
                <LogOut size={18} />
                <span>Çıkış Yap</span>
              </button>
            </div>
          </aside>

          {/* İçerik */}
          <section className="md:col-span-9">
            <div
              className="md:rounded-xl md:p-5 md:border"
              style={{ ...panel(1, S), backgroundColor: "transparent" }}
            >
              <Outlet />
            </div>
          </section>
        </div>

        <p className="mt-6 text-xs text-center" style={{ color: secondary }}>
          Her şey sizin kontrolünüzde. Sorunuz mu var?{" "}
          <span style={{ color: S.accent }}>İletişime geçin.</span>
        </p>
      </div>
    </div>
  );
};

export default CustomerShell;
